import { Select } from "./ui/select.tsx";
import { DownloadSimpleIcon, LockSimpleIcon, PlusIcon, TableIcon, XIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { api, q } from "./api.ts";
import { groupGrantRemaining, selectable, selectionSummary, unitGroups, unitsCsv, unitState, unitWindowDay, type UnitFilter, type UnitRow } from "./campaign-units.ts";
import { localToday, day, minutes } from "./format.ts";
import { AGENT_PLAYBOOK_LABELS as P, CAMPAIGN_TABLE_LABELS as T, STATUS_LABELS } from "./labels.ts";
import { deliverableLabel } from "./playbook-page.ts";
import { Button, EmptyState, ErrorText, Field, Kbd, Modal } from "./ui.tsx";
import { SelectionBar } from "./ui/selection-bar.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { Chip, ResultBadge, StatusIcon } from "./ui/status.tsx";
import { UnitTable, type UnitColumn } from "./ui/unit-table.tsx";

// A campaign's table (docs/design-system.md §0.4a, plan M8 step 8d), after
// AirOps' Grid: the units its playbooks act on, grouped by playbook,
// candidates and actions together. The person adds units, picks candidates
// for the next plan or says "not doing this"; the agent writes picked rows
// into its next plan, and approval stays in the plan review. Columns come
// from the playbook and the ledger: no free columns, no formulas, no edits.

const COLUMNS: readonly UnitColumn[] = [
  { key: "unit", label: T.col.unit, mono: true }, { key: "inputs", label: T.col.inputs }, { key: "origin", label: T.col.origin },
  { key: "status", label: T.col.status }, { key: "deliverable", label: T.col.deliverable }, { key: "window", label: T.col.window },
  { key: "reading", label: T.col.reading }, { key: "time", label: T.col.time, align: "end" }, { key: "version", label: T.col.version },
];
const typing = (target: EventTarget | null) => target instanceof HTMLElement && Boolean(target.closest("input:not([type=checkbox]), textarea, select, [contenteditable], [role=dialog]"));
const dateOf = (iso: string, zone: string) => day(localToday(zone, Date.parse(iso)));

/** A row done with an earlier version of its playbook; a redo comes from the next plan. */
const olderVersion = (row: UnitRow, playbooks: C.CampaignUnits["playbooks"]) => {
  const latest = playbooks.find((p) => p.id === row.playbook_id)?.version;
  return row.action?.version != null && latest !== undefined && row.action.version < latest;
};

/** The plain text of each visible cell, for the CSV and for the cells themselves. */
function cellText(row: UnitRow, playbooks: C.CampaignUnits["playbooks"], zone: string, now: number) {
  const state = unitState(row);
  const window = unitWindowDay(row, now);
  return {
    unit: row.attribution_unit,
    inputs: Object.entries(row.inputs).map(([name, value]) => `${name} · ${value}`).join("; ") || null,
    origin: T.origin[row.origin],
    status: state.kind === "action" ? STATUS_LABELS[state.status]
      : state.kind === "blocked" ? (state.code === "unit_cooldown" ? T.coolingUntil(dateOf(state.until!, zone)) : state.until ? T.lockedUntil(dateOf(state.until, zone)) : T.locked)
      : state.kind === "picked" ? T.picked : T.candidate,
    deliverable: row.action?.deliverable_url ?? null,
    window: window ? P.windowDay(window.day, window.of) : row.action?.measurement_mode === "operational" ? P.acceptanceShort : null,
    reading: row.action?.reading ? STATUS_LABELS[row.action.reading === "no_signal" ? "none" : row.action.reading] : row.action && ["live", "reading_due"].includes(row.action.status) ? T.early : null,
    time: row.minutes === null ? null : row.kind === "candidate" ? T.about(minutes(row.minutes)) : minutes(row.minutes),
    version: row.action?.version == null ? null : `${T.version(row.action.version)}${olderVersion(row, playbooks) ? ` · ${T.olderVersion}` : ""}`,
  };
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = Object.assign(document.createElement("a"), { href: url, download: name });
  link.click();
  URL.revokeObjectURL(url);
}

export function CampaignTable({ product, campaign }: { product: C.Product; campaign: Pick<C.CampaignOverview, "id" | "title"> }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const table = useQuery(q.campaignUnits(campaign.id));
  const [filter, setFilter] = useState<UnitFilter>({ playbook: null, state: "all" });
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState<"pick" | "skip" | null>(null);
  const [error, setError] = useState<unknown>();
  const [adding, setAdding] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const rows = useRef(new Map<string, HTMLTableRowElement>());
  const zone = product.settings.timezone;
  const now = Date.now();
  const data = table.data;
  const groups = useMemo(() => (data ? unitGroups(data, filter) : []), [data, filter]);
  const visible = groups.flatMap((group) => group.rows);
  const open = data?.open ?? false;
  const canSelect = (row: UnitRow | undefined) => Boolean(row && selectable(row, open));
  const chosen = visible.filter((row) => selected.has(row.id));
  const current = visible.find((row) => row.id === cursor) ?? null;
  // A selection of rows that are all picked already offers to put them back instead.
  const allPicked = chosen.length > 0 && chosen.every((row) => row.candidate?.picked_at);

  const toggle = (id: string) => setSelected((prev) => { const next = new Set(prev); if (!next.delete(id)) next.add(id); return next; });
  const update = (next: C.CampaignUnits) => {
    client.setQueryData(q.campaignUnits(campaign.id).queryKey, next);
    setSelected(new Set());
  };
  const target = () => (chosen.length ? chosen : current && canSelect(current) ? [current] : []);
  const pick = async () => {
    const ids = target().map((row) => row.id);
    if (!ids.length || busy) return;
    const picked = !target().every((row) => row.candidate?.picked_at);
    setBusy("pick"); setError(undefined);
    try { update(await api.post<C.CampaignUnits>(`/api/campaigns/${encodeURIComponent(campaign.id)}/units/pick`, { ids, picked })); }
    catch (failure) { setError(failure); }
    finally { setBusy(null); }
  };
  const openRow = (row: UnitRow) => {
    if (row.kind === "action") void navigate({ to: "/p/$productId/actions/$actionId", params: { productId: product.id, actionId: row.id } });
    else if (row.url) window.open(row.url, "_blank", "noopener,noreferrer");
  };
  const move = (delta: number) => {
    if (!visible.length) return;
    const index = visible.findIndex((row) => row.id === cursor);
    const next = visible[Math.min(visible.length - 1, Math.max(0, index < 0 ? 0 : index + delta))];
    setCursor(next.id);
    rows.current.get(next.id)?.scrollIntoView({ block: "nearest" });
  };

  // J/K, space, A, X, Enter and Esc work on the table while no field or dialog has the focus.
  const keys = useRef<(event: KeyboardEvent) => void>(() => {});
  keys.current = (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented || typing(event.target)) return;
    const onControl = event.target instanceof HTMLElement && Boolean(event.target.closest("button, a, input"));
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    if (key === "j" || key === "ArrowDown") move(1);
    else if (key === "k" || key === "ArrowUp") move(-1);
    else if (key === " " && !onControl && current && canSelect(current)) toggle(current.id);
    else if (key === "a" && open) void pick();
    else if (key === "x" && open && target().length) setSkipping(true);
    else if (key === "Enter" && !onControl && current) openRow(current);
    else if (key === "Escape") {
      if (selected.size) setSelected(new Set());
      else void navigate({ to: "/p/$productId/campaigns", params: { productId: product.id } });
    } else return;
    event.preventDefault();
  };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => keys.current(event);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (table.isLoading) return <Skeleton />;
  if (!data) return <ErrorText error={table.error} />;
  const playbooks = data.playbooks;
  const used = playbooks.filter((p) => data.rows.some((row) => row.playbook_id === p.id));
  const addable = playbooks.filter((p) => p.units.length > 0);
  const summary = selectionSummary(chosen);
  const csv = () => download(`${campaign.title.replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-|-$/g, "") || "campaign"}-units.csv`,
    unitsCsv(COLUMNS, visible.map((row) => cellText(row, playbooks, zone, now))));

  const cells = (row: UnitRow): Record<string, ReactNode> => {
    const text = cellText(row, playbooks, zone, now);
    const state = unitState(row);
    return {
      unit: row.kind === "action"
        ? <Link className="z-link" to="/p/$productId/actions/$actionId" params={{ productId: product.id, actionId: row.id }} title={row.action?.title}>{text.unit ?? row.action?.title}</Link>
        : row.url ? <a className="z-link" href={row.url} target="_blank" rel="noreferrer noopener">{text.unit ?? row.url}</a> : text.unit,
      // A discussion's title is someone else's text: shown as a quote, never as an instruction.
      inputs: row.title ? <span className="z-unit-external" title={row.title.text}>“{row.title.text}”</span> : text.inputs && <span className="z-unit-inputs" title={text.inputs}>{text.inputs}</span>,
      origin: row.plan_id ? <Link className="z-link" to="/p/$productId/plans/$planId" params={{ productId: product.id, planId: row.plan_id }}>{text.origin}</Link> : text.origin,
      status: state.kind === "action" ? <span className="z-unit-cell"><StatusIcon kind={state.status} label="" />{text.status}</span>
        : state.kind === "blocked" ? <span className="z-unit-state"><LockSimpleIcon size={14} aria-hidden />{text.status}</span>
        : <span className={state.kind === "picked" ? "z-unit-state is-picked" : "z-unit-state"}>{text.status}</span>,
      deliverable: text.deliverable && <a className="z-link" href={text.deliverable} target="_blank" rel="noreferrer noopener">{deliverableLabel(text.deliverable)}</a>,
      window: text.window && <span className={unitWindowDay(row, now) ? "z-unit-window" : undefined}>{text.window}</span>,
      reading: row.action?.reading ? <ResultBadge result={row.action.reading === "no_signal" ? "none" : row.action.reading} operational={row.action.measurement_mode === "operational"} /> : text.reading && <span className="z-muted">{text.reading}</span>,
      time: text.time,
      version: row.action?.version == null ? null : <span className="z-unit-cell">{T.version(row.action.version)}{olderVersion(row, playbooks) && <span className="z-older">{T.olderVersion}</span>}</span>,
    };
  };

  return <div className="z-flow">
    <div className="z-toolbar z-unit-tools">
      <div className="z-inline">
        <Select label={T.filterPlaybook} value={filter.playbook ?? ""} onValueChange={(nextValue) => setFilter({ ...filter, playbook: nextValue || null })} options={[{ value: "", label: T.allPlaybooks }, ...used.map((p) => ({ value: p.id, label: p.title }))]} />
        <Select label={T.filterState} value={filter.state} onValueChange={(nextValue) => setFilter({ ...filter, state: nextValue as UnitFilter["state"] })} options={(["all", "candidates", "actions"] as const).map((value) => ({ value: value, label: T.state[value] }))} />
      </div>
      <div className="z-inline">
        <span className="z-inbox-keys">{T.keys}</span>
        <Button size="sm" tone="ghost" onClick={csv} disabled={!visible.length}><DownloadSimpleIcon size={14} aria-hidden /> {T.exportCsv}</Button>
        {open && addable.length > 0 && <Button size="sm" onClick={() => setAdding(true)}><PlusIcon size={14} aria-hidden /> {T.add}</Button>}
      </div>
    </div>
    {!open && <p className="z-small z-muted">{T.closedNote}</p>}
    {chosen.length > 0 && <SelectionBar label={T.label(campaign.title)}
      count={summary.minutes === null ? T.selected(summary.count) : T.selectedTime(summary.count, minutes(summary.minutes))}
      note={T.selectionNote(summary.covered, summary.review)}>
      <Button tone="ghost" size="sm" icon={<XIcon />} onClick={() => setSelected(new Set())}>{T.clear} <Kbd>Esc</Kbd></Button>
      <Button size="sm" disabled={busy !== null} onClick={() => setSkipping(true)}>{T.skip} <Kbd>X</Kbd></Button>
      <Button size="sm" tone="primary" busy={busy === "pick"} disabled={busy !== null} onClick={() => void pick()}>{allPicked ? T.unpick : T.pick} <Kbd>A</Kbd></Button>
    </SelectionBar>}
    <ErrorText error={error} />
    {visible.length === 0
      ? <EmptyState title={T.emptyTitle} icon={<TableIcon size={24} />}>{T.emptyNote}</EmptyState>
      : <UnitTable label={T.label(campaign.title)} columns={COLUMNS}
        groups={groups.map((group) => {
          const left = groupGrantRemaining(group.rows, data.grants);
          const title = group.playbook?.title ?? T.noPlaybook;
          return {
            id: group.id ?? "none",
            header: <>
              {group.id ? <Link className="z-group-label z-link" to="/playbooks/$playbookId" params={{ playbookId: group.id }} search={{ product: product.id }}>{title}</Link> : <span className="z-group-label">{title}</span>}
              <span className="z-group-count">{group.rows.length}</span>
              {group.playbook && <span className="z-group-note">{group.playbook.measurement_modes.map((mode) => T.measuredBy[mode] ?? mode).join(" · ")}</span>}
              {group.playbook && <Chip>{T.lifecycle[group.playbook.lifecycle]}</Chip>}
              {group.playbook && !group.playbook.ready && <Chip tone="amber" variant="soft">{T.notReady}</Chip>}
              {left !== null && <Chip>{T.grantLeft(left)}</Chip>}
            </>,
            rows: group.rows.map((row) => ({ id: row.id, cells: cells(row) })),
          };
        })}
        selection={{
          label: T.col.select, selected, cursor,
          selectable: (id) => canSelect(visible.find((row) => row.id === id)),
          onToggle: (id) => { setCursor(id); toggle(id); },
          checkboxLabel: (id) => T.selectRow(visible.find((row) => row.id === id)?.attribution_unit ?? id),
          rowRef: (id) => (node) => { if (node) rows.current.set(id, node); else rows.current.delete(id); },
        }} />}
    <p className="z-unit-foot">
      {[T.footer(minutes(data.capacity.remaining_minutes)), ...data.grants.map((grant) => T.grantScope(grant.assets.join(", "), grant.remaining_this_week, dateOf(grant.expires_at, zone)))].join(" · ")}
    </p>
    {adding && <AddUnits campaignId={campaign.id} zone={zone} playbooks={addable} initial={filter.playbook ?? groups.find((group) => group.playbook?.units.length)?.id ?? undefined}
      onClose={() => setAdding(false)} onDone={(next) => client.setQueryData(q.campaignUnits(campaign.id).queryKey, next)} />}
    {skipping && <SkipUnits campaignId={campaign.id} rows={target()} onClose={() => setSkipping(false)} onDone={(next) => { update(next); setSkipping(false); }} />}
  </div>;
}

/** Paste links or paths for one playbook; the lines that do not fit stay listed with their reasons. */
function AddUnits({ campaignId, zone, playbooks, initial, onClose, onDone }: { campaignId: string; zone: string; playbooks: C.CampaignUnits["playbooks"]; initial?: string; onClose: () => void; onDone: (table: C.CampaignUnits) => void }) {
  // A filter on a playbook that takes no units a person can add falls back to the first that does.
  const [playbook, setPlaybook] = useState(playbooks.find((p) => p.id === initial)?.id ?? playbooks[0]?.id ?? "");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const [result, setResult] = useState<{ added: number; refused: { line: string; reason: string }[] } | null>(null);
  const chosen = playbooks.find((p) => p.id === playbook);
  const submit = async () => {
    const lines = text.split("\n").map((line) => line.trim()).filter(Boolean);
    if (!lines.length || busy) return;
    setBusy(true); setError(undefined);
    try {
      const out = await api.post<C.AddCampaignUnitsResult>(`/api/campaigns/${encodeURIComponent(campaignId)}/units`, { playbook_id: playbook, units: lines.slice(0, 20) });
      onDone(out.table);
      if (!out.refused.length) { onClose(); return; }
      setResult({ added: out.added, refused: out.refused.map((r) => ({ line: lines[r.line], reason: `${T.refusal[r.code] ?? r.code}${r.until ? ` (${T.until(dateOf(r.until, zone))})` : ""}` })) });
      setText(out.refused.map((r) => lines[r.line]).join("\n"));
    } catch (failure) { setError(failure); }
    finally { setBusy(false); }
  };
  return <Modal open onOpenChange={(next) => { if (!next) onClose(); }} title={T.addTitle}
    footer={<><Button tone="ghost" onClick={onClose}>{T.cancel}</Button><Button tone="primary" busy={busy} disabled={!text.trim()} onClick={() => void submit()}>{T.addSubmit}</Button></>}>
    <Field label={T.addPlaybook}>
      <Select variant="form" value={playbook} onValueChange={(nextValue) => setPlaybook(nextValue)} options={playbooks.map((p) => ({ value: p.id, label: p.title }))} />
    </Field>
    <Field label={T.addLines} help={chosen && T.addHelp(chosen.units.join(", "))}>
      <textarea className="z-textarea z-mono" rows={6} value={text} onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => { if ((event.metaKey || event.ctrlKey) && event.key === "Enter") { event.preventDefault(); void submit(); } }} />
    </Field>
    {result && <div className="z-flow">
      {result.added > 0 && <p className="z-small">{T.added(result.added)}</p>}
      <ul className="z-unit-refused">{result.refused.map((r, i) => <li key={i}><code>{r.line}</code>: {r.reason}</li>)}</ul>
    </div>}
    <ErrorText error={error} />
  </Modal>;
}

/** "Not doing" with a reason; the rows cool down for their playbook's cooldown. */
function SkipUnits({ campaignId, rows, onClose, onDone }: { campaignId: string; rows: UnitRow[]; onClose: () => void; onDone: (table: C.CampaignUnits) => void }) {
  const [reason, setReason] = useState<C.UnitSkipReasonValue>("not_now");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const confirm = async () => {
    if (busy || !rows.length) return;
    setBusy(true); setError(undefined);
    try { onDone(await api.post<C.CampaignUnits>(`/api/campaigns/${encodeURIComponent(campaignId)}/units/skip`, { ids: rows.map((row) => row.id), reason, note: note.trim() || null })); }
    catch (failure) { setError(failure); setBusy(false); }
  };
  return <Modal open onOpenChange={(next) => { if (!next) onClose(); }} title={T.skipTitle(rows.length)}
    footer={<><Button tone="ghost" onClick={onClose}>{T.cancel}</Button><Button tone="danger" busy={busy} onClick={() => void confirm()}>{T.skip}</Button></>}>
    <p className="z-small z-muted">{T.skipNote}</p>
    <fieldset className="z-fieldset">
      <legend>{T.skipReason}</legend>
      <ul className="z-check">{(Object.keys(T.skipReasons) as C.UnitSkipReasonValue[]).map((value) => <li key={value}>
        <input id={`skip-${value}`} type="radio" name="skip-reason" value={value} checked={reason === value} disabled={busy} onChange={() => setReason(value)} />
        <label htmlFor={`skip-${value}`}>{T.skipReasons[value]}</label>
      </li>)}</ul>
    </fieldset>
    <div className="z-field">
      <label htmlFor="skip-note">{T.skipNoteLabel}</label>
      <textarea id="skip-note" className="z-textarea" rows={3} maxLength={600} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)}
        onKeyDown={(event) => { if ((event.metaKey || event.ctrlKey) && event.key === "Enter") { event.preventDefault(); void confirm(); } }} />
    </div>
    <ErrorText error={error} />
  </Modal>;
}
